import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, CheckCircle2, AlertCircle } from "lucide-react";
import type {
  IBulkImportPreviewRow,
  IBulkImportRow,
} from "@/interface/bulkImport.interface";
import EditRowModal from "./EditRowModal";
import { getFieldValue } from "./bulkImport.utils";

interface ImportPreviewTableProps {
  headers: string[];
  validRows: IBulkImportPreviewRow[];
  invalidRows: IBulkImportPreviewRow[];
  onUpdateRow: (rowIndex: number, updatedData: IBulkImportRow) => void;
}

/**
 * Preview table for bulk import.
 * Shows valid rows on top (white) and invalid rows at bottom (light red).
 * Each invalid row has an edit button to open the EditRowModal.
 */
const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  headers,
  validRows,
  invalidRows,
  onUpdateRow,
}) => {
  const [editingRow, setEditingRow] = useState<IBulkImportPreviewRow | null>(
    null
  );

  const totalRows = validRows.length + invalidRows.length;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Table Header Summary */}
      <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-slate-50/50">
        <div className="flex items-center gap-4 text-xs font-semibold">
          <span className="text-slate-600">
            Total Rows: <strong className="text-slate-800">{totalRows}</strong>
          </span>
          <span className="text-emerald-600 flex items-center gap-1">
            <CheckCircle2 size={14} /> Valid:{" "}
            <strong>{validRows.length}</strong>
          </span>
          <span className="text-red-500 flex items-center gap-1">
            <AlertCircle size={14} /> Invalid:{" "}
            <strong>{invalidRows.length}</strong>
          </span>
        </div>
      </div>

      {/* Responsive Table Container */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
        <div className="min-w-full inline-block align-middle">
          <table className="min-w-full divide-y divide-slate-200">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-16">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {/* ── Valid Rows ── */}
              {validRows?.map((row) => (
                <tr
                  key={`valid-${row.rowIndex}`}
                  className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                >
                  <td className="px-4 py-2.5 text-slate-500 font-medium whitespace-nowrap">
                    {row.rowIndex}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full text-emerald-700 bg-emerald-50 border border-emerald-200">
                      <CheckCircle2 size={12} /> Valid
                    </span>
                  </td>
                  {headers.map((header) => {
                    const val = getFieldValue(row.data, header);
                    return (
                      <td
                        key={`${row.rowIndex}-${header}`}
                        className="px-4 py-2.5 text-slate-700 whitespace-nowrap max-w-[200px] truncate"
                      >
                        {val !== "" && val !== null && val !== undefined ? String(val) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="text-xs text-slate-400">—</span>
                  </td>
                </tr>
              ))}

              {/* ── Separator ── */}
              {validRows.length > 0 && invalidRows.length > 0 && (
                <tr>
                  <td
                    colSpan={headers.length + 3}
                    className="px-4 py-1.5 bg-red-50/50 border-y border-red-100"
                  >
                    <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">
                      ⚠ Invalid Rows — Please fix the errors below
                    </span>
                  </td>
                </tr>
              )}

              {/* ── Invalid Rows ── */}
              {invalidRows.map((row) => {
                const errorFieldsLower = new Set(
                  row.errors.map((e) => e.field.toLowerCase().trim())
                );
                const headerSetLower = new Set(
                  headers.map((h) => h.toLowerCase().trim())
                );
                const generalErrors = row.errors.filter(
                  (e) => !headerSetLower.has(e.field.toLowerCase().trim()) || e.field.toLowerCase() === "general"
                );

                return (
                  <React.Fragment key={`invalid-${row.rowIndex}`}>
                    <tr className="bg-red-50/60 border-b border-red-100 hover:bg-red-50 transition-colors">
                      <td className="px-4 py-2.5 text-red-500 font-medium whitespace-nowrap">
                        {row.rowIndex}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full text-red-700 bg-red-100 border border-red-200 w-fit">
                            <AlertCircle size={12} /> Invalid
                          </span>
                          {generalErrors.length > 0 && (
                            <span className="text-[11px] font-semibold text-red-600 max-w-[200px] whitespace-normal">
                              {generalErrors.map((ge) => ge.message).join(" • ")}
                            </span>
                          )}
                        </div>
                      </td>

                      {headers?.map((header) => {
                        const headerLower = header.toLowerCase().trim();
                        const hasError = errorFieldsLower.has(headerLower);
                        const errorMsg = row.errors.find(
                          (e) => e.field.toLowerCase().trim() === headerLower
                        )?.message;
                        const val = getFieldValue(row.data, header);
                        return (
                          <td
                            key={`${row.rowIndex}-${header}`}
                            className={`px-4 py-2.5 whitespace-nowrap max-w-[200px] truncate ${hasError
                              ? "text-red-700 font-semibold bg-red-100/50"
                              : "text-slate-700"
                              }`}
                            title={hasError ? errorMsg : undefined}
                          >

                            <div className="flex flex-col">
                              <span>{val !== "" && val !== null && val !== undefined ? String(val) : "—"}</span>
                              {hasError && (
                                <span className="text-[10px] text-red-600 font-medium mt-0.5 truncate">
                                  {errorMsg}
                                </span>
                              )}
                            </div>
                          </td>

                        );
                      })}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setEditingRow(row)}
                        >
                          <Pencil size={12} /> Edit
                        </Button>
                      </td>

                    </tr>
                  </React.Fragment>
                );
              })}

              {/* ── Empty State ── */}
              {totalRows === 0 && (
                <tr>
                  <td
                    colSpan={headers.length + 3}
                    className="px-4 py-12 text-center text-slate-400"
                  >
                    No data to preview
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      <EditRowModal
        open={!!editingRow}
        onClose={() => setEditingRow(null)}
        row={editingRow}
        headers={headers}
        onSave={onUpdateRow}
      />
    </div>
  );
};

export default ImportPreviewTable;
